import { useEffect, useMemo } from "react"
import { usePlansStore } from "../store/plans-store"
import { productsService } from "../api"
import type { ProductPlan } from "../types"

function detectMultiCurrency(): boolean {
  try {
    if (new URLSearchParams(window.location.search).get("multiCurrency") === "true") {
      localStorage.setItem("multiCurrency", "true")
      return true
    }
    return localStorage.getItem("multiCurrency") === "true"
  } catch {
    return false
  }
}

export interface UsePlansResult {
  plans: ProductPlan[]
  status: "idle" | "loading" | "ready" | "error"
  error: string | null
  isBackendConfigured: boolean
}

/**
 * Fetches (and caches) the requested plans, returning them in the requested order.
 * No-op when the plans backend is not configured — callers should fall back to static config.
 */
export function usePlans(ids: string[]): UsePlansResult {
  const key = useMemo(
    () =>
      Array.from(new Set(ids.filter(Boolean)))
        .sort()
        .join(","),
    [ids],
  )
  const plansById = usePlansStore((s) => s.plansById)
  const status = usePlansStore((s) => s.status)
  const error = usePlansStore((s) => s.error)
  const loadPlans = usePlansStore((s) => s.loadPlans)

  useEffect(() => {
    if (!productsService.isConfigured || key === "") return
    loadPlans(key.split(","), { multiCurrency: detectMultiCurrency() })
  }, [key, loadPlans])

  const plans = useMemo(
    () => ids.map((id) => plansById[id]).filter(Boolean) as ProductPlan[],
    [ids, plansById],
  )

  return { plans, status, error, isBackendConfigured: productsService.isConfigured }
}
